import {
  DEFAULT_MAP_FRAME_ID,
  type LayoutDocument,
  type LayoutElement,
  type LayoutGraphicArrowheads,
  type LayoutGraphicElement,
  type LayoutGraphicFill,
  type LayoutGraphicGeometry,
  type LayoutGraphicShapeParams,
  type LayoutGraphicStroke,
  type LayoutGraphicType,
  type LayoutPage,
  type LayoutSurroundElement,
  type LegendElement,
  type LegendFrameSymbol,
  type LegendItemOverride,
  type LegendSpacing,
  type LegendStyleId,
  type LegendTextPart,
  type LegendTextSymbols,
  type MapFrameCamera,
  type MapFrameElement,
  type NorthArrowElement,
  type NorthArrowHalo,
  type NorthArrowMarkerLayer,
  type NorthArrowStyleId,
  type NorthArrowSymbol,
  type PagePresetId,
  type PageRect,
  type PageUnits,
  type ScaleBarElement,
  type ScaleBarTextSymbol,
  type ScaleBarUnitSettings,
  type ScaleBarStyleId,
  type ScaleBarUnits,
  type TableFrameElement,
  type TableFrameField,
  type TableFrameStyleId,
  type LayoutPictureElement,
  type LayoutPictureSource,
} from './layoutTypes';
import { clampRectToPage, translateRect } from './elementGeometry';
import {
  pictureNativeSizeMm,
  placePictureRect,
} from './layoutPicture';
import { normalizeLayoutGraphicGeometry } from './layoutGraphicGeometry';
import { pagePreset } from './pagePresets';
import { mmToPt, ptToMm } from './pageUnits';
import {
  effectiveScaleBarDenominator,
  SCALE_BAR_CAPTION_ROOM,
  scaleBarElementLayout,
} from './scaleBarGeometry';
import { scaleBarStyleMeta } from './scaleBarStyles';
import { BLACK, WHITE } from './symbolColors';
import { LEGEND_STYLES, legendStyleMeta } from './legendStyles';
import { tableFrameStyleMeta } from './tableFrameStyles';

/** Page ink on white paper, matching ArcGIS's default black north arrow. */
export const DEFAULT_NORTH_ARROW_SYMBOL: NorthArrowSymbol = {
  color: BLACK,
  angle: 0,
  angleAlignment: 'map',
  marker: {
    visible: true,
    source: 'style',
    formId: 'circle',
    fontGlyph: 'N',
    fileDataUrl: null,
    fileName: null,
    fillColor: BLACK,
    outlineColor: BLACK,
    outlineWidthPt: 0,
    respectFrame: true,
    scaleProportionally: true,
  },
  halo: {
    enabled: false,
    color: WHITE,
    outlineColor: BLACK,
    sizePt: 1.5,
  },
};

export const DEFAULT_LAYOUT_TEXT_SYMBOL: ScaleBarTextSymbol = {
  color: BLACK,
  sizePt: 9,
  fontFamily: 'Arial',
  fontStyle: 'regular',
  outlineColor: BLACK,
  outlineWidthPt: 0,
  underline: false,
  strikethrough: false,
  textCase: 'normal',
  positionAdjustment: 'normal',
  horizontalAlignment: 'left',
  verticalAlignment: 'baseline',
  offsetXPt: 0,
  offsetYPt: 0,
  angle: 0,
  halo: {
    enabled: false,
    color: WHITE,
    sizePt: 1,
  },
  shadow: {
    enabled: false,
    color: BLACK,
    offsetXPt: 1,
    offsetYPt: 1,
  },
  callout: {
    enabled: false,
    fillColor: WHITE,
    outlineColor: BLACK,
    paddingPt: 1,
  },
  opacityPercent: 100,
};

export const DEFAULT_SCALE_BAR_TEXT_SYMBOL = DEFAULT_LAYOUT_TEXT_SYMBOL;

export const DEFAULT_LAYOUT_GRAPHIC_STROKE: LayoutGraphicStroke = {
  color: BLACK,
  widthPt: 1,
  style: 'solid',
  cap: 'round',
  join: 'round',
  opacityPercent: 100,
};

export const DEFAULT_LAYOUT_GRAPHIC_FILL: LayoutGraphicFill = {
  color: WHITE,
  opacityPercent: 100,
  rule: 'nonzero',
};

export const DEFAULT_LAYOUT_GRAPHIC_ARROWHEADS: LayoutGraphicArrowheads = {
  start: 'none',
  end: 'closed',
  sizePt: 8,
};

export const DEFAULT_LAYOUT_GRAPHIC_SHAPE: LayoutGraphicShapeParams = {
  cornerRadius: 0.1,
  innerRatio: 0.35,
  cloudLobes: 8,
  arcStartDeg: 0,
  arcEndDeg: 180,
  tension: 0.5,
};

const DEFAULT_LEGEND_SPACING: LegendSpacing = {
  titleGapPt: 5,
  groupLayerNameGapPt: 2,
  layerNameGapPt: 2,
  headingGapPt: 2,
  classGapPt: 2,
  patchLabelGapPt: 4,
  columnGapPt: 12,
};

function defaultLegendFrame(
  enabled: boolean,
  color: string,
): LegendFrameSymbol {
  return {
    enabled,
    color,
    outlineColor: BLACK,
    outlineWidthPt: 0,
    gapXPt: 4,
    gapYPt: 4,
    offsetXPt: 2,
    offsetYPt: 2,
  };
}

function defaultLegendTextSymbols(styleId: LegendStyleId): LegendTextSymbols {
  const meta = legendStyleMeta(styleId);
  const text = (sizePt: number, fontStyle: ScaleBarTextSymbol['fontStyle']) => ({
    ...DEFAULT_LAYOUT_TEXT_SYMBOL,
    sizePt,
    fontStyle,
  });
  return {
    title: {
      ...text(meta.titleSizePt, 'bold'),
      horizontalAlignment: meta.titleAlignment,
    },
    groupLayerName: text(meta.textSizePt, 'bold'),
    layerName: text(meta.textSizePt, 'bold'),
    heading: text(meta.textSizePt, 'bold'),
    label: text(meta.textSizePt, 'regular'),
    description: text(Math.max(6, meta.textSizePt - 1), 'regular'),
  };
}

export function normalizeLegendElement(element: LegendElement): LegendElement {
  const styleId = element.styleId ?? 'centerTitle';
  const meta = legendStyleMeta(styleId);
  const defaults = defaultLegendTextSymbols(styleId);
  const textSymbols = Object.fromEntries(
    (Object.keys(defaults) as LegendTextPart[]).map((part) => {
      const current = element.textSymbols?.[part];
      return [
        part,
        {
          ...defaults[part],
          ...current,
          halo: { ...defaults[part].halo, ...current?.halo },
          shadow: { ...defaults[part].shadow, ...current?.shadow },
          callout: { ...defaults[part].callout, ...current?.callout },
        },
      ];
    }),
  ) as unknown as LegendTextSymbols;
  return {
    ...element,
    styleId,
    name: element.name ?? 'Legend',
    visible: element.visible ?? true,
    locked: element.locked ?? false,
    title: element.title ?? 'Legend',
    showTitle: element.showTitle ?? true,
    fittingStrategy: element.fittingStrategy ?? meta.fittingStrategy,
    columnCount: element.columnCount ?? meta.columnCount,
    equalColumnWidths: element.equalColumnWidths ?? false,
    minimumFontSizePt: element.minimumFontSizePt ?? 6,
    wrapLabels: element.wrapLabels ?? false,
    labelWrapWidthPt: element.labelWrapWidthPt ?? 96,
    wrapDescriptions: element.wrapDescriptions ?? false,
    descriptionWrapWidthPt: element.descriptionWrapWidthPt ?? 120,
    spacing: { ...DEFAULT_LEGEND_SPACING, ...element.spacing },
    background: {
      ...defaultLegendFrame(meta.background.enabled, meta.background.color),
      ...element.background,
    },
    border: {
      ...defaultLegendFrame(meta.border.enabled, meta.border.color),
      ...element.border,
    },
    shadow: {
      ...defaultLegendFrame(false, BLACK),
      ...element.shadow,
    },
    textSymbols,
    itemOverrides: element.itemOverrides ?? {},
  };
}

const MIN_NORTH_ARROW_MM = 4;
const MAX_NORTH_ARROW_MM = 400;

/** ArcGIS reports symbol size in points; the page stores millimetres. */
export function northArrowSizePt(arrow: NorthArrowElement): number {
  return mmToPt(arrow.rect.height);
}

let idSeq = 0;

export function nextLayoutId(prefix: string): string {
  idSeq += 1;
  return `${prefix}-${idSeq}-${Math.random().toString(36).slice(2, 8)}`;
}

export function pageFromPreset(
  presetId: Exclude<PagePresetId, 'custom'>,
): LayoutPage {
  const preset = pagePreset(presetId);
  return {
    widthMm: preset.widthMm,
    heightMm: preset.heightMm,
    units: preset.units,
    presetId,
  };
}

const DEFAULT_CAMERA: MapFrameCamera = {
  center: [0, 0],
  zoom: 2,
  bearing: 0,
  scaleDenominator: null,
};

export function createDefaultLayout(
  camera?: Partial<MapFrameCamera>,
  presetId: Exclude<PagePresetId, 'custom'> = 'a4-portrait',
): LayoutDocument {
  const page = pageFromPreset(presetId);
  const margin = 15;
  const mapFrame: MapFrameElement = {
    id: DEFAULT_MAP_FRAME_ID,
    type: 'mapFrame',
    rect: {
      x: margin,
      y: margin,
      width: page.widthMm - margin * 2,
      height: page.heightMm - margin * 2 - 40,
    },
    camera: { ...DEFAULT_CAMERA, ...camera },
    lockScale: false,
    source: 'active-map',
  };
  return {
    id: nextLayoutId('layout'),
    page,
    elements: [mapFrame],
  };
}

export function findMapFrame(
  document: LayoutDocument,
  id = DEFAULT_MAP_FRAME_ID,
): MapFrameElement | null {
  const found = document.elements.find(
    (el): el is MapFrameElement => el.type === 'mapFrame' && el.id === id,
  );
  return found ?? null;
}

export function primaryMapFrame(document: LayoutDocument): MapFrameElement | null {
  return document.elements.find((el): el is MapFrameElement => el.type === 'mapFrame') ?? null;
}

const TEXT_GRAPHICS = new Set<LayoutGraphicType>([
  'rectangleText', 'polygonText', 'straightText', 'curvedText', 'circleText', 'ellipseText',
]);
const LINE_GRAPHICS = new Set<LayoutGraphicType>([
  'line', 'curvedLine', 'freehandLine', 'arc',
]);
const POLYGON_GRAPHICS = new Set<LayoutGraphicType>([
  'rectangle', 'polygon', 'circle', 'ellipse', 'lasso', 'roundedRectangle',
  'triangle', 'rightTriangle', 'halfCircle', 'cross', 'x', 'cloud',
]);
const ARROW_GRAPHICS = new Set<LayoutGraphicType>([
  'arrow', 'curvedArrow', 'doubleArrow', 'curvedDoubleArrow',
]);

function defaultGraphicGeometry(graphicType: LayoutGraphicType): LayoutGraphicGeometry {
  if (graphicType === 'point') return { type: 'point', point: { x: 0.5, y: 0.5 } };
  if (LINE_GRAPHICS.has(graphicType) || ARROW_GRAPHICS.has(graphicType)
    || graphicType === 'straightText' || graphicType === 'curvedText') {
    return {
      type: 'path',
      paths: [{
        closed: false,
        points: graphicType === 'curvedLine' || graphicType === 'curvedArrow'
          || graphicType === 'curvedDoubleArrow' || graphicType === 'curvedText'
          ? [{ x: 0, y: 0.75 }, { x: 0.5, y: 0.25 }, { x: 1, y: 0.75 }]
          : [{ x: 0, y: 0.5 }, { x: 1, y: 0.5 }],
      }],
    };
  }
  if (graphicType === 'polygon' || graphicType === 'polygonText'
    || graphicType === 'triangle') {
    return {
      type: 'path',
      paths: [{
        closed: true,
        points: [{ x: 0.5, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }],
      }],
    };
  }
  if (graphicType === 'rightTriangle') {
    return {
      type: 'path',
      paths: [{
        closed: true,
        points: [{ x: 0, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }],
      }],
    };
  }
  return { type: 'rect', rect: { x: 0, y: 0, width: 1, height: 1 } };
}

function graphicCategory(
  graphicType: LayoutGraphicType,
): LayoutGraphicElement['category'] {
  if (TEXT_GRAPHICS.has(graphicType)) return 'text';
  if (LINE_GRAPHICS.has(graphicType)) return 'line';
  if (POLYGON_GRAPHICS.has(graphicType)) return 'polygon';
  if (ARROW_GRAPHICS.has(graphicType)) return 'arrow';
  return 'point';
}

export interface AddLayoutGraphicOptions {
  readonly rect?: PageRect;
  readonly geometry?: LayoutGraphicGeometry;
  readonly name?: string;
}

export function createLayoutGraphicElement(
  graphicType: LayoutGraphicType,
  options: AddLayoutGraphicOptions = {},
): LayoutGraphicElement {
  const common = {
    id: nextLayoutId('graphic'),
    type: 'graphic' as const,
    graphicType,
    rect: options.rect ?? { x: 25, y: 25, width: 50, height: 30 },
    rotation: 0,
    flipX: false,
    flipY: false,
    name: options.name ?? graphicType,
    visible: true,
    locked: false,
    geometry: normalizeLayoutGraphicGeometry(
      options.geometry ?? defaultGraphicGeometry(graphicType),
    ),
  };
  const category = graphicCategory(graphicType);
  if (category === 'text') {
    return {
      ...common,
      category,
      graphicType: graphicType as Extract<LayoutGraphicElement, { category: 'text' }>['graphicType'],
      text: {
        text: 'Text',
        overflow: 'shrink',
        columnCount: 1,
        columnGapPt: 6,
        marginPt: 2,
        pathOffset: 0,
        reversePath: false,
      },
      textSymbol: DEFAULT_LAYOUT_TEXT_SYMBOL,
      stroke: DEFAULT_LAYOUT_GRAPHIC_STROKE,
      fill: { ...DEFAULT_LAYOUT_GRAPHIC_FILL, color: null },
      shape: DEFAULT_LAYOUT_GRAPHIC_SHAPE,
    };
  }
  if (category === 'line') {
    return {
      ...common,
      category,
      graphicType: graphicType as Extract<LayoutGraphicElement, { category: 'line' }>['graphicType'],
      stroke: DEFAULT_LAYOUT_GRAPHIC_STROKE,
      arrowheads: { ...DEFAULT_LAYOUT_GRAPHIC_ARROWHEADS, end: 'none' },
      shape: DEFAULT_LAYOUT_GRAPHIC_SHAPE,
    };
  }
  if (category === 'point') {
    return {
      ...common,
      category,
      graphicType: 'point',
      marker: {
        shape: 'circle',
        sizePt: 10,
        fill: { ...DEFAULT_LAYOUT_GRAPHIC_FILL, color: BLACK },
        stroke: DEFAULT_LAYOUT_GRAPHIC_STROKE,
      },
    };
  }
  if (category === 'arrow') {
    return {
      ...common,
      category,
      graphicType: graphicType as Extract<LayoutGraphicElement, { category: 'arrow' }>['graphicType'],
      stroke: DEFAULT_LAYOUT_GRAPHIC_STROKE,
      fill: { ...DEFAULT_LAYOUT_GRAPHIC_FILL, color: BLACK },
      arrowheads: {
        ...DEFAULT_LAYOUT_GRAPHIC_ARROWHEADS,
        start: graphicType === 'doubleArrow' || graphicType === 'curvedDoubleArrow'
          ? 'closed'
          : 'none',
      },
      shape: DEFAULT_LAYOUT_GRAPHIC_SHAPE,
    };
  }
  return {
    ...common,
    category,
    graphicType: graphicType as Extract<LayoutGraphicElement, { category: 'polygon' }>['graphicType'],
    stroke: DEFAULT_LAYOUT_GRAPHIC_STROKE,
    fill: DEFAULT_LAYOUT_GRAPHIC_FILL,
    shape: DEFAULT_LAYOUT_GRAPHIC_SHAPE,
  };
}

/** Supplies fields missing from layouts saved before static graphics existed. */
export function normalizeLayoutGraphicElement(
  element: LayoutGraphicElement,
): LayoutGraphicElement {
  const defaults = createLayoutGraphicElement(element.graphicType, {
    rect: element.rect,
    geometry: element.geometry,
    name: element.name,
  });
  const merged = {
    ...defaults,
    ...element,
    category: graphicCategory(element.graphicType),
    rotation: Number.isFinite(element.rotation) ? ((element.rotation % 360) + 360) % 360 : 0,
    flipX: element.flipX ?? false,
    flipY: element.flipY ?? false,
    name: element.name ?? element.graphicType,
    visible: element.visible ?? true,
    locked: element.locked ?? false,
    geometry: normalizeLayoutGraphicGeometry(
      element.geometry ?? defaultGraphicGeometry(element.graphicType),
    ),
  };
  if (merged.category === 'point') {
    const base = defaults as Extract<LayoutGraphicElement, { category: 'point' }>;
    const source = element as Partial<Extract<LayoutGraphicElement, { category: 'point' }>>;
    return {
      ...merged,
      category: 'point',
      graphicType: 'point',
      marker: {
        ...base.marker,
        ...source.marker,
        fill: { ...base.marker.fill, ...source.marker?.fill },
        stroke: { ...base.marker.stroke, ...source.marker?.stroke },
      },
    };
  }
  if (merged.category === 'text') {
    const source = element as Partial<Extract<LayoutGraphicElement, { category: 'text' }>>;
    const base = defaults as Extract<LayoutGraphicElement, { category: 'text' }>;
    return {
      ...merged,
      category: 'text',
      graphicType: element.graphicType as typeof base.graphicType,
      text: { ...base.text, ...source.text },
      textSymbol: mergeLayoutTextSymbol(base.textSymbol, source.textSymbol),
      stroke: { ...base.stroke, ...source.stroke },
      fill: { ...base.fill, ...source.fill },
      shape: { ...base.shape, ...source.shape },
    };
  }
  if (merged.category === 'line') {
    const base = defaults as Extract<LayoutGraphicElement, { category: 'line' }>;
    const source = element as Partial<Extract<LayoutGraphicElement, { category: 'line' }>>;
    return {
      ...merged,
      category: 'line',
      graphicType: element.graphicType as typeof base.graphicType,
      stroke: { ...base.stroke, ...source.stroke },
      arrowheads: { ...base.arrowheads, ...source.arrowheads },
      shape: { ...base.shape, ...source.shape },
    };
  }
  if (merged.category === 'arrow') {
    const base = defaults as Extract<LayoutGraphicElement, { category: 'arrow' }>;
    const source = element as Partial<Extract<LayoutGraphicElement, { category: 'arrow' }>>;
    return {
      ...merged,
      category: 'arrow',
      graphicType: element.graphicType as typeof base.graphicType,
      stroke: { ...base.stroke, ...source.stroke },
      fill: { ...base.fill, ...source.fill },
      arrowheads: { ...base.arrowheads, ...source.arrowheads },
      shape: { ...base.shape, ...source.shape },
    };
  }
  const base = defaults as Extract<LayoutGraphicElement, { category: 'polygon' }>;
  const source = element as Partial<Extract<LayoutGraphicElement, { category: 'polygon' }>>;
  return {
    ...merged,
    category: 'polygon',
    graphicType: element.graphicType as typeof base.graphicType,
    stroke: { ...base.stroke, ...source.stroke },
    fill: { ...base.fill, ...source.fill },
    shape: { ...base.shape, ...source.shape },
  };
}

export function normalizeLayoutDocument(document: LayoutDocument): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type === 'graphic') return normalizeLayoutGraphicElement(element);
      if (element.type === 'picture') return normalizeLayoutPictureElement(element);
      if (element.type === 'legend') return normalizeLegendElement(element);
      return element;
    }),
  };
}

export interface AddLayoutPictureOptions {
  readonly rect?: PageRect;
  readonly name?: string;
  readonly preserveAspectRatio?: boolean;
}

export function createLayoutPictureElement(
  source: LayoutPictureSource,
  options: AddLayoutPictureOptions = {},
  pageWidthMm = 210,
  pageHeightMm = 297,
): LayoutPictureElement {
  const native = pictureNativeSizeMm(source.naturalWidthPx, source.naturalHeightPx);
  const rect = options.rect ?? placePictureRect(
    pageWidthMm,
    pageHeightMm,
    native.widthMm,
    native.heightMm,
    { x: 15, y: 15 },
  );
  return {
    id: nextLayoutId('picture'),
    type: 'picture',
      name: options.name ?? (source.name.replace(/\.[^.]+$/, '') || 'Picture'),
    visible: true,
    locked: false,
    rect,
    rotation: 0,
    flipX: false,
    flipY: false,
    preserveAspectRatio: options.preserveAspectRatio ?? true,
    opacityPercent: 100,
    source,
    background: defaultLegendFrame(false, WHITE),
    border: defaultLegendFrame(false, BLACK),
    shadow: defaultLegendFrame(false, BLACK),
  };
}

export function normalizeLayoutPictureElement(
  element: LayoutPictureElement,
): LayoutPictureElement {
  const defaults = createLayoutPictureElement(element.source, {
    rect: element.rect,
    name: element.name,
    preserveAspectRatio: element.preserveAspectRatio,
  });
  return {
    ...defaults,
    ...element,
    visible: element.visible ?? true,
    locked: element.locked ?? false,
    rotation: Number.isFinite(element.rotation) ? element.rotation : 0,
    flipX: Boolean(element.flipX),
    flipY: Boolean(element.flipY),
    preserveAspectRatio: element.preserveAspectRatio ?? true,
    opacityPercent: Math.max(0, Math.min(100, element.opacityPercent ?? 100)),
    source: element.source,
    background: { ...defaults.background, ...element.background },
    border: { ...defaults.border, ...element.border },
    shadow: { ...defaults.shadow, ...element.shadow },
  };
}

export function addLayoutPicture(
  document: LayoutDocument,
  source: LayoutPictureSource,
  options: AddLayoutPictureOptions = {},
): LayoutDocument {
  const element = createLayoutPictureElement(
    source,
    options,
    document.page.widthMm,
    document.page.heightMm,
  );
  return {
    ...document,
    elements: [
      ...document.elements,
      {
        ...element,
        rect: clampRectToPage(element.rect, document.page.widthMm, document.page.heightMm),
      },
    ],
  };
}

export interface LayoutPicturePatch extends Partial<
  Pick<
    LayoutPictureElement,
    | 'name'
    | 'visible'
    | 'locked'
    | 'rotation'
    | 'flipX'
    | 'flipY'
    | 'preserveAspectRatio'
    | 'opacityPercent'
    | 'source'
  >
> {
  readonly background?: Partial<LegendFrameSymbol>;
  readonly border?: Partial<LegendFrameSymbol>;
  readonly shadow?: Partial<LegendFrameSymbol>;
}

export function updateLayoutPicture(
  document: LayoutDocument,
  pictureId: string,
  patch: LayoutPicturePatch,
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type !== 'picture' || element.id !== pictureId) return element;
      return normalizeLayoutPictureElement({
        ...element,
        ...patch,
        background: { ...element.background, ...patch.background },
        border: { ...element.border, ...patch.border },
        shadow: { ...element.shadow, ...patch.shadow },
      });
    }),
  };
}

export function addLayoutGraphic(
  document: LayoutDocument,
  graphicType: LayoutGraphicType,
  options: AddLayoutGraphicOptions = {},
): LayoutDocument {
  const element = createLayoutGraphicElement(graphicType, options);
  return {
    ...document,
    elements: [
      ...document.elements,
      { ...element, rect: clampRectToPage(element.rect, document.page.widthMm, document.page.heightMm) },
    ],
  };
}

export function updateElementRect(
  document: LayoutDocument,
  elementId: string,
  rect: PageRect,
): LayoutDocument {
  const current = document.elements.find((element) => element.id === elementId);
  if (!current || ('locked' in current && current.locked)) return document;
  const clamped = clampRectToPage(rect, document.page.widthMm, document.page.heightMm);
  return {
    ...document,
    elements: document.elements.map((el) =>
      el.id === elementId ? { ...el, rect: clamped } : el,
    ),
  };
}

export function moveElement(
  document: LayoutDocument,
  elementId: string,
  dx: number,
  dy: number,
): LayoutDocument {
  const current = document.elements.find((el) => el.id === elementId);
  if (!current) return document;
  return updateElementRect(document, elementId, translateRect(current.rect, dx, dy));
}

export function updateMapFrameCamera(
  document: LayoutDocument,
  frameId: string,
  camera: MapFrameCamera,
): LayoutDocument {
  const updated: LayoutDocument = {
    ...document,
    elements: document.elements.map((el) =>
      el.type === 'mapFrame' && el.id === frameId ? { ...el, camera } : el,
    ),
  };
  return fitOwnedScaleBarWidths(updated);
}

function fitOwnedScaleBarWidths(document: LayoutDocument): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (
        element.type !== 'scaleBar'
        || (
          element.fittingStrategy !== 'adjustWidth'
          && element.fittingStrategy !== 'fixedBarWidth'
        )
      ) return element;
      const frame = document.elements.find(
        (candidate): candidate is MapFrameElement =>
          candidate.type === 'mapFrame'
          && candidate.id === element.mapFrameId,
      );
      if (!frame) return element;
      const denominator = effectiveScaleBarDenominator(element, frame.camera);
      const geometry = scaleBarElementLayout(element, denominator);
      if (!geometry) return element;
      const drawnWidth = Math.max(
        geometry.upper.widthMm,
        geometry.lower?.widthMm ?? 0,
      );
      const width = drawnWidth / SCALE_BAR_CAPTION_ROOM;
      return {
        ...element,
        rect: clampRectToPage(
          { ...element.rect, width },
          document.page.widthMm,
          document.page.heightMm,
        ),
      };
    }),
  };
}

export function setPagePreset(
  document: LayoutDocument,
  presetId: Exclude<PagePresetId, 'custom'>,
): LayoutDocument {
  const page = pageFromPreset(presetId);
  return {
    ...document,
    page,
    elements: document.elements.map((el) => ({
      ...el,
      rect: clampRectToPage(el.rect, page.widthMm, page.heightMm),
    })),
  };
}

export function setCustomPageSize(
  document: LayoutDocument,
  widthMm: number,
  heightMm: number,
  units: PageUnits,
): LayoutDocument {
  if (
    !Number.isFinite(widthMm)
    || !Number.isFinite(heightMm)
    || widthMm < 10
    || heightMm < 10
    || widthMm > 5000
    || heightMm > 5000
  ) {
    return document;
  }
  const page: LayoutPage = {
    widthMm,
    heightMm,
    units,
    presetId: 'custom',
  };
  return {
    ...document,
    page,
    elements: document.elements.map((element) => ({
      ...element,
      rect: clampRectToPage(element.rect, widthMm, heightMm),
    })),
  };
}

export interface LayoutGraphicPatch extends Partial<
  Pick<
    LayoutGraphicElement,
    | 'name'
    | 'visible'
    | 'locked'
    | 'rotation'
    | 'flipX'
    | 'flipY'
    | 'geometry'
  >
> {
  readonly text?: Partial<Extract<LayoutGraphicElement, { category: 'text' }>['text']>;
  readonly textSymbol?: ScaleBarTextSymbolPatch;
  readonly stroke?: Partial<LayoutGraphicStroke>;
  readonly fill?: Partial<LayoutGraphicFill>;
  readonly arrowheads?: Partial<LayoutGraphicArrowheads>;
  readonly shape?: Partial<LayoutGraphicShapeParams>;
  readonly marker?: Partial<Extract<LayoutGraphicElement, { category: 'point' }>['marker']>;
}

export function updateLayoutGraphic(
  document: LayoutDocument,
  graphicId: string,
  patch: LayoutGraphicPatch,
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type !== 'graphic' || element.id !== graphicId) {
        return element;
      }
      // A locked graphic must remain immutable, except for unlocking it from
      // the properties pane. Without this exception a lock is irreversible.
      if (element.locked) {
        return patch.locked === false ? { ...element, locked: false } : element;
      }
      const scalar = {
        ...element,
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.visible !== undefined ? { visible: patch.visible } : {}),
        ...(patch.locked !== undefined ? { locked: patch.locked } : {}),
        ...(patch.rotation !== undefined ? { rotation: patch.rotation } : {}),
        ...(patch.flipX !== undefined ? { flipX: patch.flipX } : {}),
        ...(patch.flipY !== undefined ? { flipY: patch.flipY } : {}),
        ...(patch.geometry ? { geometry: patch.geometry } : {}),
      };
      if (element.category === 'text') {
        return normalizeLayoutGraphicElement({
          ...scalar,
          category: 'text',
          graphicType: element.graphicType,
          text: { ...element.text, ...patch.text },
          textSymbol: mergeLayoutTextSymbol(element.textSymbol, patch.textSymbol),
          stroke: { ...element.stroke, ...patch.stroke },
          fill: { ...element.fill, ...patch.fill },
          shape: { ...element.shape, ...patch.shape },
        });
      }
      if (element.category === 'point') {
        return normalizeLayoutGraphicElement({
          ...scalar,
          category: 'point',
          graphicType: 'point',
          marker: {
            ...element.marker,
            ...patch.marker,
            fill: { ...element.marker.fill, ...patch.marker?.fill },
            stroke: { ...element.marker.stroke, ...patch.marker?.stroke },
          },
        });
      }
      return normalizeLayoutGraphicElement({
        ...scalar,
        ...(patch.stroke && 'stroke' in element
          ? { stroke: { ...element.stroke, ...patch.stroke } }
          : {}),
        ...(patch.fill && 'fill' in element
          ? { fill: { ...element.fill, ...patch.fill } }
          : {}),
        ...(patch.arrowheads && 'arrowheads' in element
          ? { arrowheads: { ...element.arrowheads, ...patch.arrowheads } }
          : {}),
        ...(patch.shape && 'shape' in element
          ? { shape: { ...element.shape, ...patch.shape } }
          : {}),
      } as LayoutGraphicElement);
    }),
  };
}

export function setElementLocked(
  document: LayoutDocument,
  elementId: string,
  locked: boolean,
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) =>
      element.id === elementId && 'locked' in element ? { ...element, locked } : element),
  };
}

export function duplicateElement(
  document: LayoutDocument,
  elementId: string,
  offset = 5,
): LayoutDocument {
  const source = document.elements.find((element) => element.id === elementId);
  if (!source || source.type === 'mapFrame') return document;
  const clone = {
    ...source,
    id: nextLayoutId(source.type === 'graphic' ? 'graphic' : source.type),
    name: 'name' in source ? `${source.name} copy` : undefined,
    locked: false,
    rect: clampRectToPage(
      translateRect(source.rect, offset, offset),
      document.page.widthMm,
      document.page.heightMm,
    ),
  } as LayoutElement;
  return { ...document, elements: [...document.elements, clone] };
}

export type LayoutElementOrder = number | 'front' | 'back' | 'forward' | 'backward';

export function reorderElement(
  document: LayoutDocument,
  elementId: string,
  order: LayoutElementOrder,
): LayoutDocument {
  const from = document.elements.findIndex((element) => element.id === elementId);
  if (from < 0 || document.elements[from].type === 'mapFrame') return document;
  const firstOverlay = document.elements.findIndex((element) => element.type !== 'mapFrame');
  const min = firstOverlay < 0 ? document.elements.length : firstOverlay;
  const max = document.elements.length - 1;
  const target = typeof order === 'number'
    ? Math.max(min, Math.min(max, Math.floor(order)))
    : order === 'front'
      ? max
      : order === 'back'
        ? min
        : order === 'forward'
          ? Math.min(max, from + 1)
          : Math.max(min, from - 1);
  if (target === from) return document;
  const elements = [...document.elements];
  const [element] = elements.splice(from, 1);
  elements.splice(target, 0, element);
  return { ...document, elements };
}

export function rotateLayoutGraphic(
  document: LayoutDocument,
  graphicId: string,
  degrees: number,
): LayoutDocument {
  if (!Number.isFinite(degrees)) return document;
  const element = document.elements.find((candidate) => candidate.id === graphicId);
  if (element?.type === 'graphic') {
    return updateLayoutGraphic(document, graphicId, { rotation: element.rotation + degrees });
  }
  if (element?.type === 'picture') {
    return updateLayoutPicture(document, graphicId, { rotation: element.rotation + degrees });
  }
  return document;
}

export const rotateElement = rotateLayoutGraphic;

export function flipLayoutGraphic(
  document: LayoutDocument,
  graphicId: string,
  axis: 'horizontal' | 'vertical',
): LayoutDocument {
  const element = document.elements.find((candidate) => candidate.id === graphicId);
  if (element?.type === 'graphic') {
    return updateLayoutGraphic(document, graphicId, axis === 'horizontal'
      ? { flipX: !element.flipX }
      : { flipY: !element.flipY });
  }
  if (element?.type === 'picture') {
    return updateLayoutPicture(document, graphicId, axis === 'horizontal'
      ? { flipX: !element.flipX }
      : { flipY: !element.flipY });
  }
  return document;
}

export const flipElement = flipLayoutGraphic;

export function removeElement(
  document: LayoutDocument,
  elementId: string,
): LayoutDocument {
  const target = document.elements.find((el) => el.id === elementId);
  if (
    !target
    || target.type === 'mapFrame'
    || ('locked' in target && target.locked)
  ) return document;
  return {
    ...document,
    elements: document.elements.filter((el) => el.id !== elementId),
  };
}

export function addNorthArrow(
  document: LayoutDocument,
  styleId: NorthArrowStyleId,
  mapFrameId = DEFAULT_MAP_FRAME_ID,
): LayoutDocument {
  const frame = findMapFrame(document, mapFrameId);
  if (!frame) return document;
  const element: LayoutSurroundElement = {
    id: nextLayoutId('north'),
    type: 'northArrow',
    mapFrameId,
    styleId,
    symbol: DEFAULT_NORTH_ARROW_SYMBOL,
    rotationFollowsFrame: true,
    name: 'North Arrow',
    visible: true,
    locked: false,
    maximumScaleDenominator: null,
    minimumScaleDenominator: null,
    orientation: 'true',
    calibrationAngle: 0,
    rect: {
      x: frame.rect.x + frame.rect.width - 28,
      y: frame.rect.y + 8,
      width: 22,
      height: 28,
    },
  };
  return { ...document, elements: [...document.elements, element] };
}

export function updateNorthArrowProperties(
  document: LayoutDocument,
  arrowId: string,
  patch: Partial<
    Pick<
      NorthArrowElement,
      | 'name'
      | 'visible'
      | 'locked'
      | 'maximumScaleDenominator'
      | 'minimumScaleDenominator'
      | 'mapFrameId'
      | 'orientation'
      | 'calibrationAngle'
      | 'styleId'
    >
  >,
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) =>
      element.type === 'northArrow' && element.id === arrowId
        ? { ...element, ...patch }
        : element,
    ),
  };
}

export interface NorthArrowSymbolPatch {
  readonly color?: string | null;
  readonly angle?: number;
  readonly angleAlignment?: NorthArrowSymbol['angleAlignment'];
  readonly marker?: Partial<NorthArrowMarkerLayer>;
  readonly halo?: Partial<NorthArrowHalo>;
}

export function updateNorthArrowSymbol(
  document: LayoutDocument,
  arrowId: string,
  patch: NorthArrowSymbolPatch,
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type !== 'northArrow' || element.id !== arrowId) return element;
      const current = element.symbol ?? DEFAULT_NORTH_ARROW_SYMBOL;
      const marker = {
        ...DEFAULT_NORTH_ARROW_SYMBOL.marker,
        ...current.marker,
        ...patch.marker,
        ...(patch.color !== undefined ? { fillColor: patch.color } : {}),
      };
      const symbol: NorthArrowSymbol = {
        ...current,
        ...(patch.color !== undefined
          ? { color: patch.color }
          : patch.marker?.fillColor !== undefined
            ? { color: patch.marker.fillColor }
            : {}),
        ...(patch.angle !== undefined && Number.isFinite(patch.angle)
          ? { angle: ((patch.angle % 360) + 360) % 360 }
          : {}),
        ...(patch.angleAlignment ? { angleAlignment: patch.angleAlignment } : {}),
        marker,
        halo: { ...current.halo, ...patch.halo },
      };
      return { ...element, symbol };
    }),
  };
}

/**
 * Size is authoritative for the drawn symbol, so it drives the element box —
 * the same two-way relationship ArcGIS keeps between symbol size and frame.
 */
export function setNorthArrowSizePt(
  document: LayoutDocument,
  arrowId: string,
  sizePt: number,
): LayoutDocument {
  const arrow = document.elements.find(
    (element): element is NorthArrowElement =>
      element.type === 'northArrow' && element.id === arrowId,
  );
  if (!arrow || !Number.isFinite(sizePt)) return document;
  const heightMm = Math.max(
    MIN_NORTH_ARROW_MM,
    Math.min(MAX_NORTH_ARROW_MM, ptToMm(sizePt)),
  );
  const factor = heightMm / arrow.rect.height;
  if (!Number.isFinite(factor) || factor <= 0) return document;
  const width = arrow.rect.width * factor;
  return updateElementRect(document, arrowId, {
    // Grow about the centre so the symbol stays where it was placed.
    x: arrow.rect.x + (arrow.rect.width - width) / 2,
    y: arrow.rect.y + (arrow.rect.height - heightMm) / 2,
    width,
    height: heightMm,
  });
}

export function addScaleBar(
  document: LayoutDocument,
  styleId: ScaleBarStyleId,
  units: ScaleBarUnits,
  mapFrameId = DEFAULT_MAP_FRAME_ID,
): LayoutDocument {
  const frame = findMapFrame(document, mapFrameId);
  if (!frame) return document;
  const meta = scaleBarStyleMeta(styleId);
  const effectiveUnits = meta.dual ? 'dual' : units;
  const upperUnit = meta.defaultUnits?.[0]
    ?? (effectiveUnits === 'imperial' ? 'miles' : 'kilometers');
  const lowerUnit = meta.defaultUnits?.[1] ?? null;
  const width = Math.min(80, frame.rect.width);
  const element: LayoutSurroundElement = {
    id: nextLayoutId('scale'),
    type: 'scaleBar',
    mapFrameId,
    styleId,
    units: effectiveUnits,
    name: meta.dual ? 'Dual Scale Bar' : 'Scale Bar',
    visible: true,
    locked: false,
    computeAtCenter: true,
    upper: {
      unit: upperUnit,
      divisionValue: null,
      divisions: meta.divisions,
      subdivisions: 1,
    },
    lower: lowerUnit
      ? {
          unit: lowerUnit,
          divisionValue: null,
          divisions: meta.divisions,
          subdivisions: 1,
        }
      : null,
    horizontalOffsetPt: 3,
    verticalOffsetPt: 0,
    fittingStrategy: 'adjustDivision',
    fixedBarWidthMm: width * 0.68,
    textSymbol: DEFAULT_SCALE_BAR_TEXT_SYMBOL,
    rect: {
      x: frame.rect.x + 8,
      y: frame.rect.y + frame.rect.height - 24,
      width,
      // Matches the bar artwork's aspect so numbers and unit caption fit.
      height: Math.round((width * (meta.dual ? 64 : 44)) / 200),
    },
  };
  return { ...document, elements: [...document.elements, element] };
}

export interface ScaleBarTextSymbolPatch extends Partial<
  Omit<ScaleBarTextSymbol, 'halo' | 'shadow' | 'callout'>
> {
  readonly halo?: Partial<ScaleBarTextSymbol['halo']>;
  readonly shadow?: Partial<ScaleBarTextSymbol['shadow']>;
  readonly callout?: Partial<ScaleBarTextSymbol['callout']>;
}

export interface ScaleBarPropertiesPatch extends Partial<
  Pick<
    ScaleBarElement,
    | 'name'
    | 'visible'
    | 'locked'
    | 'mapFrameId'
    | 'styleId'
    | 'units'
    | 'computeAtCenter'
    | 'horizontalOffsetPt'
    | 'verticalOffsetPt'
    | 'fittingStrategy'
    | 'fixedBarWidthMm'
  >
> {
  readonly upper?: Partial<ScaleBarUnitSettings>;
  readonly lower?: Partial<ScaleBarUnitSettings> | null;
  readonly textSymbol?: ScaleBarTextSymbolPatch;
}

export function updateScaleBarProperties(
  document: LayoutDocument,
  scaleBarId: string,
  patch: ScaleBarPropertiesPatch,
): LayoutDocument {
  const updated: LayoutDocument = {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type !== 'scaleBar' || element.id !== scaleBarId) return element;
      const textSymbol = {
        ...DEFAULT_SCALE_BAR_TEXT_SYMBOL,
        ...element.textSymbol,
        ...patch.textSymbol,
        halo: {
          ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.halo,
          ...element.textSymbol?.halo,
          ...patch.textSymbol?.halo,
        },
        shadow: {
          ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.shadow,
          ...element.textSymbol?.shadow,
          ...patch.textSymbol?.shadow,
        },
        callout: {
          ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.callout,
          ...element.textSymbol?.callout,
          ...patch.textSymbol?.callout,
        },
      };
      return {
        ...element,
        ...patch,
        upper: { ...element.upper, ...patch.upper },
        lower:
          patch.lower === null
            ? null
            : patch.lower
              ? {
                  ...(element.lower ?? {
                    unit: 'meters' as const,
                    divisionValue: null,
                    divisions: element.upper.divisions,
                    subdivisions: element.upper.subdivisions,
                  }),
                  ...patch.lower,
                }
              : element.lower,
        textSymbol,
      };
    }),
  };
  return fitOwnedScaleBarWidths(updated);
}

export function addLegend(
  document: LayoutDocument,
  styleIdOrMapFrameId: LegendStyleId | string = 'centerTitle',
  requestedMapFrameId?: string,
): LayoutDocument {
  const style = LEGEND_STYLES.find((candidate) => candidate.id === styleIdOrMapFrameId);
  const styleId = style?.id ?? 'centerTitle';
  const mapFrameId = style
    ? requestedMapFrameId ?? DEFAULT_MAP_FRAME_ID
    : styleIdOrMapFrameId;
  const frame = findMapFrame(document, mapFrameId);
  if (!frame) return document;
  const meta = legendStyleMeta(styleId);
  const element: LayoutSurroundElement = {
    id: nextLayoutId('legend'),
    type: 'legend',
    mapFrameId,
    styleId,
    name: 'Legend',
    visible: true,
    locked: false,
    title: 'Legend',
    showTitle: true,
    fittingStrategy: meta.fittingStrategy,
    columnCount: meta.columnCount,
    equalColumnWidths: false,
    minimumFontSizePt: 6,
    wrapLabels: false,
    labelWrapWidthPt: 96,
    wrapDescriptions: false,
    descriptionWrapWidthPt: 120,
    spacing: DEFAULT_LEGEND_SPACING,
    background: defaultLegendFrame(meta.background.enabled, meta.background.color),
    border: defaultLegendFrame(meta.border.enabled, meta.border.color),
    shadow: defaultLegendFrame(false, BLACK),
    textSymbols: defaultLegendTextSymbols(styleId),
    itemOverrides: {},
    rect: {
      x: document.page.widthMm - 62,
      y: document.page.heightMm - 90,
      width: meta.columnCount > 1 ? 76 : 48,
      height: 70,
    },
  };
  return { ...document, elements: [...document.elements, element] };
}

export interface LegendPropertiesPatch extends Partial<
  Pick<
    LegendElement,
    | 'name'
    | 'visible'
    | 'locked'
    | 'mapFrameId'
    | 'styleId'
    | 'layerIds'
    | 'title'
    | 'showTitle'
    | 'fittingStrategy'
    | 'columnCount'
    | 'equalColumnWidths'
    | 'minimumFontSizePt'
    | 'wrapLabels'
    | 'labelWrapWidthPt'
    | 'wrapDescriptions'
    | 'descriptionWrapWidthPt'
  >
> {
  readonly spacing?: Partial<LegendSpacing>;
  readonly background?: Partial<LegendFrameSymbol>;
  readonly border?: Partial<LegendFrameSymbol>;
  readonly shadow?: Partial<LegendFrameSymbol>;
  readonly layerId?: string;
  readonly item?: LegendItemOverride;
  readonly textPart?: LegendTextPart;
  readonly textSymbol?: ScaleBarTextSymbolPatch;
}

export function updateLegendProperties(
  document: LayoutDocument,
  legendId: string,
  patch: LegendPropertiesPatch,
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type !== 'legend' || element.id !== legendId) return element;
      const {
        layerId: _layerId,
        item: _item,
        textPart: _textPart,
        textSymbol: _textSymbol,
        spacing: _spacing,
        background: _background,
        border: _border,
        shadow: _shadow,
        ...elementPatch
      } = patch;
      const legend: LegendElement = {
        ...element,
        ...elementPatch,
        spacing: {
          ...DEFAULT_LEGEND_SPACING,
          ...element.spacing,
          ...patch.spacing,
        },
        background: {
          ...defaultLegendFrame(false, WHITE),
          ...element.background,
          ...patch.background,
        },
        border: {
          ...defaultLegendFrame(false, BLACK),
          ...element.border,
          ...patch.border,
        },
        shadow: {
          ...defaultLegendFrame(false, BLACK),
          ...element.shadow,
          ...patch.shadow,
        },
        textSymbols: {
          ...defaultLegendTextSymbols(element.styleId ?? 'centerTitle'),
          ...element.textSymbols,
          ...(patch.textPart && patch.textSymbol
            ? {
                [patch.textPart]: {
                  ...DEFAULT_LAYOUT_TEXT_SYMBOL,
                  ...element.textSymbols?.[patch.textPart],
                  ...patch.textSymbol,
                  halo: {
                    ...DEFAULT_LAYOUT_TEXT_SYMBOL.halo,
                    ...element.textSymbols?.[patch.textPart]?.halo,
                    ...patch.textSymbol.halo,
                  },
                  shadow: {
                    ...DEFAULT_LAYOUT_TEXT_SYMBOL.shadow,
                    ...element.textSymbols?.[patch.textPart]?.shadow,
                    ...patch.textSymbol.shadow,
                  },
                  callout: {
                    ...DEFAULT_LAYOUT_TEXT_SYMBOL.callout,
                    ...element.textSymbols?.[patch.textPart]?.callout,
                    ...patch.textSymbol.callout,
                  },
                },
              }
            : {}),
        },
      };
      if (!patch.layerId || !patch.item) return legend;
      return {
        ...legend,
        itemOverrides: {
          ...legend.itemOverrides,
          [patch.layerId]: {
            ...legend.itemOverrides?.[patch.layerId],
            ...patch.item,
          },
        },
      };
    }),
  };
}

function defaultTableField(name: string, index: number): TableFrameField {
  return {
    name,
    alias: name,
    visible: index < 10,
    widthPt: 72,
    alignment: 'left',
    headingAlignment: 'center',
    wrapHeading: false,
    wrapValues: false,
    numberFormat: 'automatic',
    decimalPlaces: 2,
    sortDirection: 'none',
    sortOrder: index,
  };
}

export function addTableFrame(
  document: LayoutDocument,
  styleId: TableFrameStyleId = 'grayDoubleAlternatingRows',
  sourceLayerId: string | null = null,
  mapFrameId = DEFAULT_MAP_FRAME_ID,
): LayoutDocument {
  const frame = findMapFrame(document, mapFrameId);
  if (!frame) return document;
  const style = tableFrameStyleMeta(styleId);
  const headingTextSymbol: ScaleBarTextSymbol = {
    ...DEFAULT_LAYOUT_TEXT_SYMBOL,
    sizePt: 10,
    fontStyle: style.boldHeading ? 'bold' : 'regular',
    color: style.headingColor,
    horizontalAlignment: 'center',
  };
  const element: TableFrameElement = {
    id: nextLayoutId('table'),
    type: 'tableFrame',
    mapFrameId,
    sourceLayerId,
    styleId,
    name: 'Table Frame',
    visible: true,
    locked: false,
    query: 'all',
    maximumRows: 100,
    fittingStrategy: 'adjustColumns',
    minimumFontSizePt: 6,
    balanceColumns: false,
    frameColumnGapPt: 8,
    showHeading: true,
    fields: [],
    headingTextSymbol,
    rowTextSymbol: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL,
      sizePt: 9,
    },
    frame: {
      ...defaultLegendFrame(true, WHITE),
      outlineColor: style.frameBorder,
      outlineWidthPt: style.frameBorder === 'transparent' ? 0 : 1,
    },
    headingBackgroundColor: style.headingBackground,
    headingBorderColor: style.headingBorder,
    headingUnderlineColor: style.headingUnderline,
    rowBorderColor: style.rowBorder,
    rowBackground1: style.rowBackground1,
    rowBackground2: style.rowBackground2,
    rowBackground1Count: style.background1Count,
    rowBackground2Count: style.background2Count,
    rect: {
      x: Math.max(8, document.page.widthMm - 150),
      y: Math.max(8, document.page.heightMm - 105),
      width: Math.min(135, document.page.widthMm - 16),
      height: Math.min(90, document.page.heightMm - 16),
    },
  };
  return { ...document, elements: [...document.elements, element] };
}

export interface TableFramePropertiesPatch extends Partial<
  Pick<
    TableFrameElement,
    | 'mapFrameId'
    | 'sourceLayerId'
    | 'styleId'
    | 'name'
    | 'visible'
    | 'locked'
    | 'query'
    | 'maximumRows'
    | 'fittingStrategy'
    | 'minimumFontSizePt'
    | 'balanceColumns'
    | 'frameColumnGapPt'
    | 'showHeading'
    | 'fields'
    | 'headingBackgroundColor'
    | 'headingBorderColor'
    | 'headingUnderlineColor'
    | 'rowBorderColor'
    | 'rowBackground1'
    | 'rowBackground2'
    | 'rowBackground1Count'
    | 'rowBackground2Count'
  >
> {
  readonly frame?: Partial<LegendFrameSymbol>;
  readonly headingTextSymbol?: ScaleBarTextSymbolPatch;
  readonly rowTextSymbol?: ScaleBarTextSymbolPatch;
  readonly fieldName?: string;
  readonly field?: Partial<TableFrameField>;
}

function mergeLayoutTextSymbol(
  current: ScaleBarTextSymbol,
  patch?: ScaleBarTextSymbolPatch,
): ScaleBarTextSymbol {
  return {
    ...DEFAULT_LAYOUT_TEXT_SYMBOL,
    ...current,
    ...patch,
    halo: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.halo,
      ...current.halo,
      ...patch?.halo,
    },
    shadow: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.shadow,
      ...current.shadow,
      ...patch?.shadow,
    },
    callout: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.callout,
      ...current.callout,
      ...patch?.callout,
    },
  };
}

export function updateTableFrameProperties(
  document: LayoutDocument,
  tableFrameId: string,
  patch: TableFramePropertiesPatch,
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type !== 'tableFrame' || element.id !== tableFrameId) {
        return element;
      }
      const {
        frame: _frame,
        headingTextSymbol: _headingTextSymbol,
        rowTextSymbol: _rowTextSymbol,
        fieldName: _fieldName,
        field: _field,
        ...scalar
      } = patch;
      return {
        ...element,
        ...scalar,
        frame: { ...element.frame, ...patch.frame },
        headingTextSymbol: mergeLayoutTextSymbol(
          element.headingTextSymbol,
          patch.headingTextSymbol,
        ),
        rowTextSymbol: mergeLayoutTextSymbol(
          element.rowTextSymbol,
          patch.rowTextSymbol,
        ),
        fields:
          patch.fieldName && patch.field
            ? element.fields.map((field) =>
                field.name === patch.fieldName
                  ? { ...field, ...patch.field }
                  : field,
              )
            : element.fields,
      };
    }),
  };
}

export function syncTableFrameFields(
  document: LayoutDocument,
  tableFrameId: string,
  names: readonly string[],
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type !== 'tableFrame' || element.id !== tableFrameId) {
        return element;
      }
      const current = new Map(element.fields.map((field) => [field.name, field]));
      return {
        ...element,
        fields: names.map(
          (name, index) => current.get(name) ?? defaultTableField(name, index),
        ),
      };
    }),
  };
}

export function replaceElement(
  document: LayoutDocument,
  element: LayoutElement,
): LayoutDocument {
  return {
    ...document,
    elements: document.elements.map((el) => (el.id === element.id ? element : el)),
  };
}
